import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { Home, Baby, FileText, TrendingUp, Search, Info, LogOut, Menu, X, Globe } from './Icons';
import logo from '../assets/logo.png';
import styles from './Navbar.module.css';

function Navbar() {
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const drawerRef = useRef(null);
  const profileRef = useRef(null);

  const isID = i18n.language === 'id';
  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/landing');
  };

  const links = [
    { to: '/', label: t('nav.home'), icon: <Home size={18} strokeWidth={1.8} aria-hidden="true" /> },
    { to: '/child', label: t('nav.child'), icon: <Baby size={18} strokeWidth={1.8} aria-hidden="true" /> },
    { to: '/log', label: t('nav.log'), icon: <FileText size={18} strokeWidth={1.8} aria-hidden="true" /> },
    { to: '/progress', label: t('nav.progress'), icon: <TrendingUp size={18} strokeWidth={1.8} aria-hidden="true" /> },
    { to: '/screening', label: t('nav.screening'), icon: <Search size={18} strokeWidth={1.8} aria-hidden="true" /> },
    { to: '/info', label: t('nav.info'), icon: <Info size={18} strokeWidth={1.8} aria-hidden="true" /> },
  ];

  // Drawer: Escape + kunci scroll + fokus
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    drawerRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Dropdown profil: Escape + klik luar
  useEffect(() => {
    if (!profileOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setProfileOpen(false); };
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [profileOpen]);

  // Tutup keduanya saat pindah halaman
  useEffect(() => { setMenuOpen(false); setProfileOpen(false); }, [location.pathname]);

  return (
    <nav className={styles.nav}>
      <div className={styles.inner}>
        {/* Logo */}
        <button className={styles.logo} onClick={() => navigate('/')}
          aria-label={isID ? 'Ke Beranda' : 'Go to Home'}>
          <img src={logo} alt="" />
          <span className={styles.logoText}>
            <span className={styles.logoName}>NOWGUT</span>
            <span className={styles.logoTagline}>
              {isID ? 'Pemantau Risiko Saluran Cerna Anak' : 'Child GI Risk Monitor'}
            </span>
          </span>
        </button>

        {/* Desktop links */}
        {!isMobile && (
          <div className={styles.links}>
            {links.map(l => (
              <Link key={l.to} to={l.to}
                className={`${styles.link} ${isActive(l.to) ? styles.linkActive : ''}`}
                aria-current={isActive(l.to) ? 'page' : undefined}>
                {l.icon}{l.label}
              </Link>
            ))}
          </div>
        )}

        {/* Right */}
        <div className={styles.right}>
          <button onClick={() => i18n.changeLanguage(isID ? 'en' : 'id')}
            className={styles.langBtn}
            aria-label={isID ? 'Ubah ke Bahasa Inggris' : 'Switch to Indonesian'}>
            <Globe size={18} aria-hidden="true" />
            {isID ? 'EN' : 'ID'}
          </button>

          {/* Profil dropdown (desktop) */}
          {!isMobile && (
            <div className={styles.profileWrap} ref={profileRef}>
              <button className={styles.profileBtn}
                onClick={() => setProfileOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                aria-label={isID ? 'Menu profil' : 'Profile menu'}>
                {/* placeholder inisial — ganti dengan inisial user kalau ada */}
                <span aria-hidden="true">N</span>
              </button>

              {profileOpen && (
                <div className={styles.dropdown} role="menu">
                  <button className={styles.dropdownItem} role="menuitem"
                    onClick={handleLogout}>
                    <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Hamburger (mobile) */}
          {isMobile && (
            <button className={styles.hamburger}
              onClick={() => setMenuOpen(true)}
              aria-label={isID ? 'Buka menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="nav-drawer">
              <Menu size={22} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {isMobile && menuOpen && (
        <>
          <div className={styles.overlay} onClick={() => setMenuOpen(false)} aria-hidden="true" />
          <div id="nav-drawer" ref={drawerRef} tabIndex={-1}
            className={styles.drawer} role="dialog" aria-modal="true"
            aria-label={isID ? 'Menu navigasi' : 'Navigation menu'}>
            <div className={styles.drawerHeader}>
              <button className={styles.drawerClose} onClick={() => setMenuOpen(false)}
                aria-label={isID ? 'Tutup menu' : 'Close menu'}>
                <X size={22} aria-hidden="true" />
              </button>
            </div>

            {links.map(l => (
              <Link key={l.to} to={l.to}
                className={`${styles.mobileLink} ${isActive(l.to) ? styles.mobileLinkActive : ''}`}
                aria-current={isActive(l.to) ? 'page' : undefined}
                onClick={() => setMenuOpen(false)}>
                {l.icon}{l.label}
              </Link>
            ))}

            <button className={styles.mobileLogout}
              onClick={() => { handleLogout(); setMenuOpen(false); }}>
              <LogOut size={18} strokeWidth={1.8} aria-hidden="true" />
              {t('nav.logout')}
            </button>
          </div>
        </>
      )}
    </nav>
  );
}

export default Navbar;